//--- React
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
//--- Ant Design
import { Row, Col, Tabs, Modal } from "antd";
//--- Project
import { getApis, postApis } from "components/Common/apis";
import {
  vehicleDetailsApiUrl,
  vehicleTestStopApiUrl,
  vehicleTestStartApiUrl,
} from "components/Common/endpoints";
import {
  setHBResultDetails,
  setHBStartDetails,
  setDynoTestRunningStatus,
  setSelectedTab,
  setDynoStartDetails,
  setDynoStopDetails,
  setHBTestRunningStatus,
  setDynoPaginationDetails,
  setHBPaginationDetails,
  setHBPageMini,
} from "reducers/eolTestsSlice";
import { setNavStop } from "reducers/authSlice";
import { setPageLoader } from "reducers/loaderSlice";
import { useRequiredAccess } from "hooks/useRequiredAccess";
import { ORG_TYPES, ROLES } from "components/Common/Constants";
import { EOLConfirmationPopup } from "components/Common/confirmationPopup";
import Connectivity from "./Connectivity";
import Dyno from "./Dyno";
import DynoStartTest from "./Dyno/Panels/StartTest";
import HBStartTest from "./Connectivity/Panels/HBStartTest";
import { setVehicleDetails } from "reducers/eolSlice";

const EolTests = () => {
  const dispatch = useDispatch();
  const navStop = useSelector((state) => state.auth)?.navStop;
  const eol = useSelector((state) => state.eolTests);
  const vehicleSlice = useSelector((state) => state.eol);
  const vehicleDetails = vehicleSlice?.vehicleDetails;
  const selectedTab = eol?.selectedTab;
  const dynoStartDetails = eol?.dynoStartDetails;
  const HBTestRunningStatus = eol?.HBTestRunningStatus;
  const dynoTestRunningStatus = eol?.dynoTestRunningStatus;
  const [vehicle, setVehicle] = useState({
    vehId: vehicleSlice?.vehicleDetails?.vehId || null,
    vehImei: vehicleSlice?.vehicleDetails?.vehImei || null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const authToken = localStorage.getItem("authToken");
  const checkAccess = useRequiredAccess(
    [ROLES.ADMIN, ROLES.EXECUTIVE, ROLES.TEST_ENGINEER],
    [ORG_TYPES.ADMINISTRATOR, ORG_TYPES.MANUFACTURER]
  );
  const queryClient = useQueryClient();

  const showCommentModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getVehicleDetails = useCallback(async () => {
    await getApis(
      vehicleDetailsApiUrl,
      {
        vIdVal: vehicle?.vehId,
      },
      authToken
    )
      .then((response) => {
        dispatch(setVehicleDetails(response?.data));
        queryClient.invalidateQueries("hbtests", "hbresult");
      })
      .catch((e) => toast.error(e.response?.data?.message));
    dispatch(setPageLoader(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, dispatch, vehicle?.vehId]);

  useEffect(() => {
    checkAccess();
    if (vehicle?.vehImei && vehicle.vehImei !== "") {
      dispatch(setPageLoader("refresh"));
      getVehicleDetails();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVehicleDetails, vehicle?.vehImei, dispatch]);

  const onChangeImei = async (_, value) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      dispatch(
        setHBPaginationDetails({
          current: 1,
        })
      );
      dispatch(setHBPageMini(1));
      dispatch(
        setDynoPaginationDetails({
          current: 1,
        })
      );
      setVehicle(value);
      // dispatch(setVehicleDetails({}));
      dispatch(setHBStartDetails({}));
      dispatch(setHBResultDetails({}));
      dispatch(setHBTestRunningStatus(false));
      dispatch(setNavStop(false));
      dispatch(setDynoStartDetails({}));
      dispatch(setDynoTestRunningStatus(false));
      dispatch(setDynoStopDetails({}));
    }
  };

  const onChangeTab = async (key) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      dispatch(setSelectedTab(key));

      if (
        (dynoTestRunningStatus && key === "dyno") ||
        (HBTestRunningStatus && key === "heartBeat")
      ) {
        dispatch(setNavStop(true));
      } else {
        dispatch(setNavStop(false));
      }
    }
  };

  const dynoStartTest = async () => {
    await postApis(
      vehicleTestStartApiUrl,
      {
        imei: vehicleDetails?.vehImei,
        startTime: Date.now(),
        type: "DYNO",
      },
      authToken
    )
      .then((response) => {
        dispatch(setDynoStartDetails(response?.data));
        dispatch(setDynoTestRunningStatus(true));
        dispatch(setNavStop(true));
        dispatch(setDynoStopDetails({}));
      })
      .catch((e) => toast.error(e.response?.data?.message));
  };

  const dynoStopTest = async () => {
    await postApis(
      vehicleTestStopApiUrl,
      {
        imei: vehicleDetails?.vehImei,
        stopTime: Date.now(),
        testId: dynoStartDetails?.testId || vehicleDetails?.previousTests?.dyno,
      },
      authToken
    )
      .then((response) => {
        // toast.success("Test successfully completed");
        dispatch(setDynoStopDetails(response?.data));
        dispatch(setDynoTestRunningStatus(false));
        dispatch(setNavStop(false));
        if (
          response?.data?.status === "COMPLETED" &&
          !response?.data?.comment
        ) {
          showCommentModal();
        }
      })
      .catch((e) => toast.error(e.response?.data?.message));
  };

  const items = [
    {
      key: "heartBeat",
      label: `Heart Beat`,
      children: <Connectivity onChangeImei={onChangeImei} vehicle={vehicle} />,
    },
    {
      key: "dyno",
      label: `Dyno`,
      children: (
        <Dyno
          isModalOpen={isModalOpen}
          showCommentModal={showCommentModal}
          handleCloseModal={handleCloseModal}
          onChangeImei={onChangeImei}
          vehicle={vehicle}
        />
      ),
    },
  ];

  return (
    <Row gutter={16} data-testid="eol-tests">
      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
        <Tabs
          defaultActiveKey={selectedTab}
          items={items}
          activeKey={selectedTab}
          onChange={onChangeTab}
          tabBarExtraContent={
            selectedTab === "dyno" ? (
              <DynoStartTest
                stopTest={dynoStopTest}
                startTest={dynoStartTest}
              />
            ) : (
              <HBStartTest />
            )
          }
        />
      </Col>
      {contextHolder}
    </Row>
  );
};

export default EolTests;
